import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePackState } from '../../../data/pack-store';
import { openDetailWithUrl } from '../../../app/url-sync';
import { StatusIcon } from '../../icons/StatusIcon';
import { ViewTestsIcon } from './HeaderIcons';
import styles from './header.module.css';

/**
 * kaneai's "View tests" popover (TestRunSidebar inside LTAnchoredOverlay),
 * rebuilt lean over pack data: lists the run's instances; clicking one swaps
 * the open detail. Right-anchored card below the trigger button, rendered in
 * a portal on document.body with fixed positioning: inside the top bar it
 * painted under the detail header, and the TMS crumb strip (overflow:
 * hidden) clipped it entirely.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function TestListPopover(_ref) {
  var currentId = _ref.currentId,
    _ref$triggerClassName = _ref.triggerClassName,
    triggerClassName = _ref$triggerClassName === void 0 ? "".concat(styles.iconButton, " ").concat(styles.iconButtonDefault) : _ref$triggerClassName;
  var rowIds = usePackState(function (s) {
    return s.rowIds;
  });
  var rows = usePackState(function (s) {
    return s.rows;
  });
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  var rootRef = useRef(null);
  var cardRef = useRef(null);
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    anchor = _useState4[0],
    setAnchor = _useState4[1];

  // Pin the card under the trigger's right edge; it lives outside the
  // trigger's layout, so it follows the window rather than the page.
  useLayoutEffect(function () {
    if (!open) return undefined;
    var place = function place() {
      var _rootRef$current;
      var rect = (_rootRef$current = rootRef.current) === null || _rootRef$current === void 0 ? void 0 : _rootRef$current.getBoundingClientRect();
      if (!rect) return;
      // Outside the viewer's tree the card loses its inherited font, so take
      // the trigger's (standalone viewer or the TMS host).
      setAnchor({
        top: rect.bottom + 6,
        right: window.innerWidth - rect.right,
        fontFamily: rootRef.current ? getComputedStyle(rootRef.current).fontFamily : 'inherit'
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return function () {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);
  useEffect(function () {
    if (!open) return undefined;
    var onDown = function onDown(e) {
      var _rootRef$current2, _cardRef$current;
      var target = e.target;
      if (!((_rootRef$current2 = rootRef.current) !== null && _rootRef$current2 !== void 0 && _rootRef$current2.contains(target)) && !((_cardRef$current = cardRef.current) !== null && _cardRef$current !== void 0 && _cardRef$current.contains(target))) {
        setOpen(false);
      }
    };
    var onKey = function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return function () {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return /*#__PURE__*/_jsxs("div", {
    className: styles.popoverRoot,
    ref: rootRef,
    children: [/*#__PURE__*/_jsx("button", {
      type: "button",
      className: triggerClassName,
      "aria-label": "View tests",
      "aria-haspopup": "dialog",
      "aria-expanded": open,
      onClick: function onClick() {
        return setOpen(function (v) {
          return !v;
        });
      },
      children: /*#__PURE__*/_jsx(ViewTestsIcon, {})
    }), open && anchor && /*#__PURE__*/createPortal(/*#__PURE__*/_jsx("div", {
      ref: cardRef,
      className: styles.popoverCard,
      style: {
        top: anchor.top,
        right: anchor.right,
        fontFamily: anchor.fontFamily
      },
      role: "dialog",
      "aria-label": "Tests in this run",
      children: /*#__PURE__*/_jsx("ul", {
        className: styles.popoverList,
        children: rowIds.map(function (id) {
          var row = rows.get(id);
          if (!row || row.state !== 'loaded') return null;
          return /*#__PURE__*/_jsx("li", {
            children: /*#__PURE__*/_jsxs("button", {
              type: "button",
              className: "".concat(styles.popoverItem, " ").concat(id === currentId ? styles.popoverItemCurrent : ''),
              "aria-current": id === currentId ? 'true' : undefined,
              onClick: function onClick() {
                setOpen(false);
                if (id !== currentId) openDetailWithUrl(id);
              },
              children: [/*#__PURE__*/_jsx(StatusIcon, {
                status: row.status,
                size: 16
              }), /*#__PURE__*/_jsx("span", {
                className: styles.popoverItemTitle,
                title: row.title,
                children: row.title
              })]
            })
          }, id);
        })
      })
    }), document.body)]
  });
}